import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Car, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function CarDetail() {
  const { carId } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCar();
  }, [carId]);

  const fetchCar = async () => {
    try {
      const response = await axios.get(`${API}/cars/${carId}`);
      setCar(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Car not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Car className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <p className="text-xl text-gray-700">Car not found</p>
          <Link to="/gallery">
            <Button className="mt-4 bg-pink-600 hover:bg-pink-700">Back to Gallery</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/gallery">
              <Button variant="ghost" className="text-gray-700 hover:text-pink-600 hover:bg-pink-50">
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Gallery
              </Button>
            </Link>
          </div>
          <Logo size="sm" showText={true} />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 mt-8">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Photo Section */}
            <div className="bg-gray-100">
              {car.photo_data ? (
                <img
                  src={car.photo_data}
                  alt={`${car.make} ${car.model}`}
                  className="w-full h-96 object-cover"
                />
              ) : (
                <div className="w-full h-96 bg-gray-100 flex items-center justify-center">
                  <Car className="h-32 w-32 text-gray-300" />
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-lg font-semibold px-4 py-2 rounded-full bg-pink-100 text-pink-600">
                  #{car.registration_number}
                </span>
                {car.checked_in && (
                  <span className="inline-flex items-center text-sm px-3 py-1 rounded-full bg-green-100 text-green-700">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Checked In
                  </span>
                )}
              </div>

              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {car.year} {car.make} {car.model}
              </h1>

              <div className="space-y-4 text-lg mt-6">
                <div className="flex">
                  <span className="text-gray-500 w-24">Class:</span>
                  <span className="text-gray-900 font-medium">{car.class_name}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-24">Owner:</span>
                  <span className="text-gray-900 font-medium">{car.owner_name}</span>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <Link to={`/vote?car=${car.id}`}>
                  <Button className="w-full bg-pink-600 hover:bg-pink-700 py-6 text-lg">
                    Vote for This Car
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info Card */}
      </div>
    </div>
  );
}
